import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

type BirthDateInputProps = {
  onChange: (date: string) => void;
  initialValue?: string; // YYYY-MM-DD 형식
  isRequired?: boolean;
  label?: string;
  error?: string;
  setError?: (error: string) => void;
  onValidate?: () => void;
};

function BirthDateInput({
  onChange,
  initialValue = '',
  isRequired = false,
  label = '생년월일',
  error = '',
  setError,
  onValidate,
}: BirthDateInputProps) {
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [runValidation, setRunValidation] = useState<boolean>(false);

  useEffect(() => {
    // 초기값이 있는 경우 분리하여 설정
    if (initialValue) {
      const parts = initialValue.split('-');
      if (parts.length === 3) {
        setYear(parts[0]);
        setMonth(parts[1]);
        setDay(parts[2]);
      }
    }
  }, [initialValue]);

  // 공통 입력 핸들러 함수
  const handleInputChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLength: number,
  ) => {
    // 숫자만 입력 가능하고, 최대 길이 제한
    const sanitizedValue = value.replace(/\D/g, '').slice(0, maxLength);
    setter(sanitizedValue);
    return sanitizedValue;
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newYear = handleInputChange(e.target.value, setYear, 4);
    updateBirthDate(newYear, month, day);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMonth = handleInputChange(e.target.value, setMonth, 2);
    updateBirthDate(year, newMonth, day);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDay = handleInputChange(e.target.value, setDay, 2);
    updateBirthDate(year, month, newDay);
  };

  const updateBirthDate = (
    yearVal: string,
    monthVal: string,
    dayVal: string,
  ) => {
    // 모든 필드가 채워진 경우에만 상위 컴포넌트에 값 전달
    if (yearVal && monthVal && dayVal) {
      const date = `${yearVal}-${monthVal}-${dayVal}`;
      onChange(date);
    } else {
      // 필드 중 하나라도 비어있으면 빈 문자열 전달
      onChange('');
    }
  };

  // 공통으로 사용되는 유효성 검사 실행 함수
  const startValidation = () => {
    if (year && month && day && onValidate) {
      setRunValidation(true);
    }
  };

  // 숫자에 앞에 0 붙이는 함수
  const padWithZero = (value: string): string => {
    return value.length === 1 ? `0${value}` : value;
  };

  const handleYearBlur = () => {
    // 년도가 4자리가 아닌 경우 빈 문자열로 설정
    if (year.length < 4 && year !== '') {
      setYear('');
      updateBirthDate('', month, day);
      if (setError) setError('년도는 4자리로 입력해주세요');
    }

    startValidation();
  };

  const handleMonthBlur = () => {
    // 월이 한 자리 숫자인 경우 앞에 0을 붙임
    if (month.length === 1 && month !== '') {
      const paddedMonth = padWithZero(month);
      setMonth(paddedMonth);
      updateBirthDate(year, paddedMonth, day);
    }

    startValidation();
  };

  const handleDayBlur = () => {
    // 일이 한 자리 숫자인 경우 앞에 0을 붙임
    if (day.length === 1 && day !== '') {
      const paddedDay = padWithZero(day);
      setDay(paddedDay);
      updateBirthDate(year, month, paddedDay);
    }

    startValidation();
  };

  useEffect(() => {
    // 유효성 검사 로직
    if (runValidation && onValidate) {
      onValidate();
      setRunValidation(false);
    }
  }, [runValidation, onValidate]);

  return (
    <Container>
      <Label>
        {label} {isRequired && <RequiredMark>*</RequiredMark>}
      </Label>
      <InputContainer>
        <InputWrapper>
          <span>년</span>
          <BirthInput
            placeholder='YYYY'
            value={year}
            onChange={handleYearChange}
            onBlur={handleYearBlur}
            hasError={!!error}
          />
        </InputWrapper>
        <InputWrapper>
          <span>월</span>
          <BirthInput
            placeholder='MM'
            value={month}
            onChange={handleMonthChange}
            onBlur={handleMonthBlur}
            hasError={!!error}
          />
        </InputWrapper>
        <InputWrapper>
          <span>일</span>
          <BirthInput
            placeholder='DD'
            value={day}
            onChange={handleDayChange}
            onBlur={handleDayBlur}
            hasError={!!error}
          />
        </InputWrapper>
      </InputContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.div`
  margin-bottom: 6px;
  color: var(--Mono-65, #5f6165);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const RequiredMark = styled.span`
  color: var(--Secondary-Danger);
`;

const InputContainer = styled.div`
  display: flex;
  gap: 6px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > span {
    color: var(--Mono-35, #a7a7a7);
    font-family: Pretendard;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 18px */
    letter-spacing: -0.24px;
  }
`;

const BirthInput = styled.input<{ hasError?: boolean }>`
  width: 76px;

  height: 36px;
  padding: 9px 12px;
  border: 1px solid ${(props) => (props.hasError ? '#FF2D55' : 'transparent')};
  border-radius: 12px;
  background-color: #fff;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${(props) => (props.hasError ? '#FF2D55' : '#246CF6')};
  }

  &::placeholder {
    color: #a7a7a7;
  }
`;

const ErrorMessage = styled.div`
  color: #ff2d55;
  font-size: 12px;
  text-align: right; // 우측 정렬 추가
`;

export default BirthDateInput;
